import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { AccountForm } from "./forms/AccountForm";
import { CompanyForm } from "./forms/CompanyForm";
import { ScopeForm } from "./forms/ScopeForm";
import { Globe, Plus, X, Check, Trash } from "lucide-react";

interface Scope {
  code: string;
  name: string;
}

interface WebhookEntry {
  object_type: string;
  url: string;
}

interface OnboardingStep {
  title: string;
  description: string;
  image: string;
}

const steps: OnboardingStep[] = [
  {
    title: "Create Your Account",
    description:
      "Set up your personal account to get started with our platform.",
    image:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80&w=2070",
  },
  {
    title: "Company Details",
    description:
      "Tell us about your company so we can better serve your needs.",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2069",
  },
  {
    title: "Integration Scope",
    description: "Select the integration features you need for your solution.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2070",
  },
];

export function SaasOnboarding() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isExistingUser, setIsExistingUser] = useState(false);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyDetails, setCompanyDetails] = useState({
    company_name: "",
    website: "",
    contact_phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "",
    registration_number: "",
  });
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);

  // Trigger webhook related states
  const [showTriggerSection, setShowTriggerSection] = useState(false);
  const [webhooks, setWebhooks] = useState<WebhookEntry[]>([]);
  const [availableObjectTypes, setAvailableObjectTypes] = useState<Scope[]>([]);
  const [newWebhook, setNewWebhook] = useState<WebhookEntry>({
    object_type: "",
    url: "",
  });
  const [isAddingWebhook, setIsAddingWebhook] = useState(false);
  const [editingWebhookIndex, setEditingWebhookIndex] = useState<number | null>(
    null
  );
  const [editingWebhookUrl, setEditingWebhookUrl] = useState("");

  useEffect(() => {
    if (currentStep === 2) {
      loadScopes();
    }
  }, [currentStep]);

  useEffect(() => {
    // Check if "triggers" is selected to show the webhook section
    const hasTriggerScope = scopes
      .filter((scope) => selectedScopes.includes(scope.code))
      .some((scope) => scope.code.toLowerCase() === "triggers");

    setShowTriggerSection(hasTriggerScope);

    // Get selected scopes to use as object types for webhooks
    if (hasTriggerScope) {
      // Get all selected scopes except the "triggers" scope itself
      const selectedScopeObjects = scopes.filter(
        (scope) =>
          selectedScopes.includes(scope.code) &&
          scope.code.toLowerCase() !== "triggers"
      );

      // Filter out scopes that are already used in webhooks
      const usedTypes = webhooks.map((webhook) => webhook.object_type);
      const availableScopeObjects = selectedScopeObjects.filter(
        (scope) => !usedTypes.includes(scope.code)
      );

      setAvailableObjectTypes(availableScopeObjects);
    } else {
      // If triggers scope is not selected, clear available object types
      setAvailableObjectTypes([]);
    }
  }, [selectedScopes, scopes, webhooks]);

  const loadScopes = async () => {
    try {
      setLoading(true);
      const { data, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("code, name")
        .order("name");

      if (scopesError) throw scopesError;
      setScopes(data || []);
    } catch (err) {
      console.error("Error loading scopes:", err);
      setError("Failed to load integration scopes");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate("/signup");
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleUserCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsExistingUser(false);
    setLoading(true);

    try {
      // Validate required fields
      if (!firstName.trim() || !lastName.trim()) {
        throw new Error("First name and last name are required");
      }

      // Check if email is already registered
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email)
        .maybeSingle();

      if (existingProfile) {
        setIsExistingUser(true);
        setError("An account with this email already exists.");
        return;
      }

      // Create new user
      const { data: authData, error: signUpError } = await supabase.auth.signUp(
        {
          email,
          password,
        }
      );

      if (signUpError) {
        if (signUpError.message.includes("already registered")) {
          setIsExistingUser(true);
          setError("An account with this email already exists.");
          return;
        }
        throw signUpError;
      }

      if (!authData.user) {
        throw new Error("Failed to create user account");
      }

      // Create profile
      const { error: profileError } = await supabase.from("profiles").insert([
        {
          id: authData.user.id,
          email,
          user_type: "saas",
          full_name: `${firstName} ${lastName}`,
        },
      ]);

      if (profileError) throw profileError;

      // Move to next step
      handleNext();
    } catch (err: any) {
      console.error("Signup error:", err);
      setError(err.message || "An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate website URL
      if (
        companyDetails.website &&
        !companyDetails.website.match(
          /^https?:\/\/[A-Za-z0-9.-]+\.[A-Za-z]{2,}.*$/
        )
      ) {
        setError(
          "Please enter a valid website URL starting with http:// or https://"
        );
        return;
      }

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // Create SaaS details
      const { error: saasError } = await supabase.from("saas_details").insert([
        {
          id: user.id,
          company_name: companyDetails.company_name,
          website: companyDetails.website,
          contact_email: email,
          contact_phone: companyDetails.contact_phone,
          address: companyDetails.address,
          postal_code: companyDetails.postal_code,
          city: companyDetails.city,
          country: companyDetails.country,
        },
      ]);

      if (saasError) throw saasError;

      // Move to next step
      handleNext();
    } catch (err: any) {
      console.error("Company creation error:", err);
      setError(err.message || "An error occurred while saving company details");
    } finally {
      setLoading(false);
    }
  };

  const handleAddWebhook = () => {
    setIsAddingWebhook(true);
    setNewWebhook({
      object_type:
        availableObjectTypes.length > 0 ? availableObjectTypes[0].code : "",
      url: "",
    });
  };

  const handleCancelAddWebhook = () => {
    setIsAddingWebhook(false);
    setNewWebhook({ object_type: "", url: "" });
  };

  const handleSaveWebhook = () => {
    // Validate webhook
    if (!newWebhook.object_type || !newWebhook.url) {
      setError("Please select a scope and enter a webhook URL");
      return;
    }

    // Add new webhook
    setWebhooks([...webhooks, newWebhook]);

    // Reset form
    setIsAddingWebhook(false);
    setNewWebhook({ object_type: "", url: "" });
    setError("");
  };

  const handleRemoveWebhook = (index: number) => {
    // Remove the webhook
    setWebhooks(webhooks.filter((_, i) => i !== index));

    // If we were editing this webhook, cancel editing
    if (editingWebhookIndex === index) {
      setEditingWebhookIndex(null);
      setEditingWebhookUrl("");
    }
  };

  const handleEditWebhook = (index: number) => {
    setEditingWebhookIndex(index);
    setEditingWebhookUrl(webhooks[index].url);
  };

  const handleCancelEditWebhook = () => {
    setEditingWebhookIndex(null);
    setEditingWebhookUrl("");
  };

  const handleSaveEditWebhook = () => {
    if (editingWebhookIndex === null) return;

    // Validate URL
    if (!editingWebhookUrl.trim()) {
      setError("Please enter a valid webhook URL");
      return;
    }

    // Update the webhook URL
    const updatedWebhooks = [...webhooks];
    updatedWebhooks[editingWebhookIndex] = {
      ...updatedWebhooks[editingWebhookIndex],
      url: editingWebhookUrl,
    };

    setWebhooks(updatedWebhooks);
    setEditingWebhookIndex(null);
    setEditingWebhookUrl("");
    setError("");
  };

  const handleScopeSelection = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("No authenticated user found");

      // Get scope codes for selected scopes
      const selectedScopeCodes = selectedScopes;

      // Check if triggers scope is selected
      const hasTriggerScope = selectedScopes.some(
        (code) => code.toLowerCase() === "triggers"
      );

      // Prepare webhooks data only if triggers scope is selected
      let webhooksData = null;
      if (hasTriggerScope) {
        // Convert webhooks to the desired format: [{object_type: url}]
        webhooksData = webhooks.map((webhook) => {
          const entry: Record<string, string> = {};
          entry[webhook.object_type] = webhook.url;
          return entry;
        });

        // If no webhooks are configured but triggers is selected, use empty array
        if (webhooksData.length === 0) {
          webhooksData = [];
        }
      }

      // Update saas_details with selected scope codes and webhooks
      const { error: updateError } = await supabase
        .from("saas_details")
        .update({
          scopes: selectedScopeCodes,
          webhooks: webhooksData, // Will be null if triggers is not selected
        })
        .eq("id", user.id);

      if (updateError) throw updateError;

      // Navigate to SaaS dashboard
      navigate("/extranet/saas");
    } catch (err: any) {
      console.error("Scope selection error:", err);
      setError(err.message || "An error occurred while saving selected scopes");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    switch (currentStep) {
      case 0:
        return handleUserCreation(e);
      case 1:
        return handleCompanyCreation(e);
      case 2:
        return handleScopeSelection(e);
      default:
        return;
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 bg-white p-4 md:p-8 flex flex-col relative">
        <div className="flex-1">
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center ${
                    index < steps.length - 1 ? "flex-1" : ""
                  }`}
                >
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center
                      ${
                        index <= currentStep
                          ? "bg-indigo-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }
                    `}
                  >
                    {index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-0.5 mx-4
                        ${index < currentStep ? "bg-indigo-600" : "bg-gray-200"}
                      `}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step content */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {steps[currentStep].title}
            </h2>
            <p className="text-gray-500">{steps[currentStep].description}</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
              {isExistingUser && (
                <p className="mt-2 text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    Sign in here
                  </Link>
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {currentStep === 0 && (
              <AccountForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
              />
            )}

            {currentStep === 1 && (
              <CompanyForm
                companyDetails={companyDetails}
                setCompanyDetails={setCompanyDetails}
              />
            )}

            {currentStep === 2 && (
              <>
                <ScopeForm
                  scopes={scopes}
                  selectedScopes={selectedScopes}
                  toggleScope={(scopeCode) => {
                    // If we're deselecting a scope
                    if (selectedScopes.includes(scopeCode)) {
                      // If it's the "triggers" scope, clear all webhooks
                      if (scopeCode.toLowerCase() === "triggers") {
                        setWebhooks([]);
                      }
                      // If it's another scope, remove any webhooks that use this scope
                      else {
                        setWebhooks((prevWebhooks) =>
                          prevWebhooks.filter(
                            (webhook) => webhook.object_type !== scopeCode
                          )
                        );
                      }

                      // Remove the scope from selected scopes
                      setSelectedScopes((prev) =>
                        prev.filter((code) => code !== scopeCode)
                      );
                    }
                    // If we're selecting a scope, just add it
                    else {
                      setSelectedScopes((prev) => [...prev, scopeCode]);
                    }
                  }}
                  loading={loading}
                />

                {/* Trigger Webhooks Section */}
                {showTriggerSection && (
                  <div className="mt-8 border-t pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-2">
                        <Globe className="h-5 w-5 text-indigo-600" />
                        <h3 className="text-lg font-medium text-gray-700">
                          Trigger Webhooks
                        </h3>
                      </div>
                      {!isAddingWebhook && availableObjectTypes.length > 0 && (
                        <button
                          onClick={handleAddWebhook}
                          className="btn-secondary inline-flex items-center text-sm"
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          Add Webhook
                        </button>
                      )}
                    </div>

                    {/* Add Webhook Form */}
                    {isAddingWebhook && (
                      <div className="mb-6 p-4 border border-indigo-100 rounded-lg bg-indigo-50">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          Add New Webhook
                        </h4>

                        <div className="grid grid-cols-1 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Object Type
                            </label>
                            <select
                              value={newWebhook.object_type}
                              onChange={(e) =>
                                setNewWebhook({
                                  ...newWebhook,
                                  object_type: e.target.value,
                                })
                              }
                              className="w-full p-2 border border-gray-300 rounded-md"
                            >
                              {availableObjectTypes.length === 0 ? (
                                <option value="">
                                  {selectedScopes.some(
                                    (code) => code.toLowerCase() === "triggers"
                                  )
                                    ? "No available scopes - all are already configured"
                                    : "Select at least one scope to configure triggers"}
                                </option>
                              ) : (
                                availableObjectTypes.map((scope: Scope) => (
                                  <option key={scope.code} value={scope.code}>
                                    {scope.name}
                                  </option>
                                ))
                              )}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Webhook URL
                            </label>
                            <input
                              type="url"
                              value={newWebhook.url}
                              onChange={(e) =>
                                setNewWebhook({
                                  ...newWebhook,
                                  url: e.target.value,
                                })
                              }
                              placeholder="https://example.com/webhook"
                              className="w-full p-2 border border-gray-300 rounded-md"
                            />
                          </div>
                        </div>

                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={handleCancelAddWebhook}
                            className="btn-secondary text-sm"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveWebhook}
                            className="btn-primary text-sm"
                            disabled={
                              !newWebhook.object_type || !newWebhook.url
                            }
                          >
                            Add Webhook
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Webhooks List */}
                    {webhooks.length > 0 ? (
                      <div className="grid grid-cols-1 gap-3">
                        {webhooks.map((webhook, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between p-3 border rounded-lg"
                          >
                            {editingWebhookIndex === index ? (
                              <div className="w-full">
                                <div className="font-medium text-sm text-gray-800 mb-2">
                                  {scopes.find(
                                    (scope) =>
                                      scope.code === webhook.object_type
                                  )?.name || webhook.object_type}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="url"
                                    value={editingWebhookUrl}
                                    onChange={(e) =>
                                      setEditingWebhookUrl(e.target.value)
                                    }
                                    className="flex-1 p-2 text-sm border border-gray-300 rounded-md"
                                    placeholder="https://example.com/webhook"
                                  />
                                  <div className="flex space-x-2">
                                    <button
                                      onClick={handleCancelEditWebhook}
                                      className="p-1 text-gray-500 hover:text-gray-700"
                                      title="Cancel"
                                    >
                                      <X className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={handleSaveEditWebhook}
                                      className="p-1 text-green-500 hover:text-green-700"
                                      title="Save"
                                      disabled={!editingWebhookUrl.trim()}
                                    >
                                      <Check className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <div className="font-medium text-sm text-gray-800">
                                    {scopes.find(
                                      (scope) =>
                                        scope.code === webhook.object_type
                                    )?.name || webhook.object_type}
                                  </div>
                                  <div className="text-sm text-gray-500 truncate max-w-md">
                                    {webhook.url}
                                  </div>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditWebhook(index)}
                                    className="text-blue-500 hover:text-blue-700"
                                    title="Edit"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="16"
                                      height="16"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      className="h-4 w-4"
                                    >
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleRemoveWebhook(index)}
                                    className="text-red-500 hover:text-red-700"
                                    title="Delete"
                                  >
                                    <Trash className="h-4 w-4" />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 border border-dashed rounded-lg">
                        <p className="text-sm text-gray-500">
                          No webhooks configured.{" "}
                          {availableObjectTypes.length > 0
                            ? 'Click "Add Webhook" to create one.'
                            : ""}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            <div className="mt-8 flex justify-between">
              <button
                type="button"
                onClick={handleBack}
                className="btn-secondary"
              >
                Back
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {currentStep < steps.length - 1 ? "Next" : "Complete Setup"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Right side - Background Image */}
      <div className="hidden md:block md:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${steps[currentStep].image})` }}
        />
      </div>
    </div>
  );
}
